import { useEffect, useState } from "react";
import QRCode from "qrcode";

interface QrImageProps {
  value: string;
  /** Pixel edge of the generated bitmap; CSS decides the displayed size. */
  size?: number;
  className?: string;
  alt: string;
}

/**
 * Drawn in the browser instead of fetched from a QR web service. Two reasons,
 * both real: the API is served under helmet's default CSP, whose `img-src
 * 'self' data:` blocked the third-party image outright — the dialog showed a
 * broken icon — and a tester download link is a capability token, which had no
 * business being handed to an outside host just to be rendered.
 */
export function QrImage({ value, size = 360, className, alt }: QrImageProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setSrc(null);
    setError(null);

    QRCode.toDataURL(value, {
      width: size,
      margin: 1,
      // The link is scanned off a laptop screen in office light; the heavier
      // correction level survives glare and a slightly out-of-focus camera.
      errorCorrectionLevel: "M",
      color: { dark: "#101828ff", light: "#ffffffff" },
    })
      .then((dataUrl) => {
        if (live) setSrc(dataUrl);
      })
      .catch((cause: unknown) => {
        if (live) setError(cause instanceof Error ? cause.message : String(cause));
      });

    return () => {
      live = false;
    };
  }, [value, size]);

  if (error) {
    return (
      <div className={`flex items-center justify-center p-3 text-center text-[11px] text-[#B42318] ${className ?? ""}`}>
        Kode QR gagal dibuat: {error}. Gunakan tautan langsung di bawah.
      </div>
    );
  }

  if (!src) {
    return <div className={`animate-pulse rounded-lg bg-[#EAECF0] ${className ?? ""}`} />;
  }

  return <img src={src} alt={alt} className={className} />;
}
